import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { getMappedTerminalPharmacies } from '../api/cp';

function normalizeRow(row) {
  const mappingId = row?.mappingId ?? row?.mapping_id;
  if (mappingId === undefined || mappingId === null || mappingId === '') return null;
  const latitudeRaw = row?.latitude ?? row?.y_pos ?? row?.lat;
  const longitudeRaw = row?.longitude ?? row?.x_pos ?? row?.lng;
  const latitude = latitudeRaw === null || latitudeRaw === undefined || latitudeRaw === '' ? null : Number(latitudeRaw);
  const longitude = longitudeRaw === null || longitudeRaw === undefined || longitudeRaw === '' ? null : Number(longitudeRaw);
  return {
    mappingId: Number(mappingId),
    pharmacyId: row?.pharmacyId ?? row?.pharmacy_id ?? row?.id,
    institutionCode: String(row?.institutionCode ?? row?.institution_code ?? ''),
    externalCustomerCode: String(row?.externalCustomerCode ?? row?.external_customer_code ?? row?.code ?? ''),
    name: String(row?.name ?? row?.customer_name ?? ''),
    phone: String(row?.phone ?? row?.pharmacy_phone ?? ''),
    address: row?.address ?? '',
    businessNo: String(row?.businessNo ?? row?.business_no ?? ''),
    representativeName: String(row?.representativeName ?? row?.representative_name ?? ''),
    latitude: Number.isFinite(latitude) ? latitude : null,
    longitude: Number.isFinite(longitude) ? longitude : null,
  };
}

function distanceMeters(a, b) {
  const rad = (value) => value * Math.PI / 180;
  const earth = 6371000;
  const dLat = rad(b.latitude - a.latitude);
  const dLng = rad(b.longitude - a.longitude);
  const x = Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(a.latitude)) * Math.cos(rad(b.latitude)) * Math.sin(dLng / 2) ** 2;
  return earth * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

function formatDistance(value) {
  return value < 1000 ? Math.round(value) + 'm' : (value / 1000).toFixed(1) + 'km';
}

export default function MappedPharmacyDashboard() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [userLocation, setUserLocation] = useState(null);

  useEffect(() => {
    try { localStorage.removeItem('pharmacyListCache'); } catch (_) {}
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setPage(1);
    setRows([]);
  }, [debouncedQuery]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      page === 1 ? setLoading(true) : setLoadingMore(true);
      setError('');
      try {
        const data = await getMappedTerminalPharmacies({ q: debouncedQuery, page, limit: 50 });
        if (cancelled) return;
        const next = Array.isArray(data?.rows) ? data.rows.map(normalizeRow).filter(Boolean) : [];
        setRows((prev) => page === 1 ? next : [...prev, ...next.filter((row) => !prev.some((item) => item.mappingId === row.mappingId))]);
        setTotal(Number(data?.total) || next.length);
      } catch (e) {
        if (!cancelled) setError(e?.message || '담당 거래처를 불러오지 못했습니다.');
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [debouncedQuery, page]);

  const rowsWithCoordinates = useMemo(
    () => rows.filter((row) => row.latitude !== null && row.longitude !== null),
    [rows],
  );

  useEffect(() => {
    if (rowsWithCoordinates.length === 0 || !navigator.geolocation) {
      setUserLocation(null);
      return undefined;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => setUserLocation({ latitude: position.coords.latitude, longitude: position.coords.longitude }),
      () => setUserLocation(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
    );
    return undefined;
  }, [rowsWithCoordinates.length]);

  const nearby = useMemo(() => {
    if (!userLocation || rowsWithCoordinates.length === 0) return [];
    return rowsWithCoordinates
      .map((row) => ({ ...row, distance: distanceMeters(userLocation, row) }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 3);
  }, [rowsWithCoordinates, userLocation]);

  const selectForPayment = (pharmacy) => {
    navigate('/app/payment', {
      state: {
        from: '/app/dashboard',
        pharmacy,
      },
    });
  };

  return (
    <div style={styles.page}>
      {nearby.length > 0 && !debouncedQuery && (
        <section style={styles.nearbySection}>
          <h2 style={styles.heading}>가까운 담당 거래처</h2>
          {nearby.map((row) => (
            <PharmacyRow key={'nearby-' + row.mappingId} row={row} distance={row.distance} onPay={selectForPayment} highlighted />
          ))}
        </section>
      )}

      <section style={styles.allSection}>
        <h2 style={styles.heading}>담당 거래처 전체</h2>
        <div style={styles.search}>
          <Search size={18} color="#8b95a1" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="거래처코드, 약국명, 사업자번호 검색"
            style={styles.searchInput}
          />
        </div>
        <div style={styles.list}>
          {loading && <p style={styles.empty}>담당 거래처를 불러오는 중입니다.</p>}
          {!loading && error && <p style={{ ...styles.empty, color: '#e5484d' }}>{error}</p>}
          {!loading && !error && rows.length === 0 && <p style={styles.empty}>담당자에게 매칭된 약국이 없습니다.</p>}
          {!loading && rows.map((row) => <PharmacyRow key={row.mappingId} row={row} onPay={selectForPayment} />)}
          {!loading && rows.length < total && (
            <button type="button" disabled={loadingMore} onClick={() => setPage((v) => v + 1)} style={styles.moreButton}>
              {loadingMore ? '불러오는 중...' : '더 보기 (' + rows.length + '/' + total + ')'}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}

function PharmacyRow({ row, onPay, distance, highlighted = false }) {
  return (
    <div style={{ ...styles.row, ...(highlighted ? styles.highlightedRow : {}) }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <strong style={styles.rowTitle}>{row.externalCustomerCode || row.institutionCode || '-'} · {row.name}</strong>
        <span style={styles.rowMeta}>
          사업자번호 {row.businessNo || '-'}
          {distance !== undefined ? ' · ' + formatDistance(distance) : ''}
        </span>
      </div>
      <button type="button" onClick={() => onPay(row)} style={styles.payButton}>결제</button>
    </div>
  );
}

const styles = {
  page: { flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', background: '#f6f8f7' },
  nearbySection: { flexShrink: 0, padding: '14px 16px 8px' },
  allSection: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: '10px 16px 16px' },
  heading: { fontSize: 16, margin: '0 0 10px', color: '#191f28' },
  search: { flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid #e5e8eb', borderRadius: 12, padding: '0 12px', marginBottom: 10 },
  searchInput: { flex: 1, border: 0, outline: 0, padding: '12px 0', fontSize: 14, minWidth: 0 },
  list: { flex: 1, overflowY: 'auto', background: '#fff', borderRadius: 12, padding: '0 12px' },
  row: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0', borderBottom: '1px solid #f0f2f1' },
  highlightedRow: { background: '#eff8f2', borderRadius: 10, borderBottom: 0, padding: 12, marginBottom: 6 },
  rowTitle: { display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#191f28', fontSize: 14 },
  rowMeta: { display: 'block', color: '#8b95a1', fontSize: 12, marginTop: 4 },
  payButton: { flexShrink: 0, border: 0, background: 'var(--brand-primary)', color: '#fff', borderRadius: 8, padding: '7px 12px', fontWeight: 700 },
  moreButton: { width: '100%', border: 0, background: '#f2f4f6', borderRadius: 10, padding: 11, margin: '12px 0', color: '#4e5968', fontWeight: 650 },
  empty: { textAlign: 'center', color: '#8b95a1', padding: '42px 8px' },
};
